import { useLocation } from 'wouter';

// Type definitions for navigation utilities
interface NavigateToMessagesOptions {
  sessionId?: string;
  replace?: boolean;
}

interface ParsedQuery {
  sessionId?: string;
  [key: string]: string | undefined;
}

/**
 * Hook to provide navigation utilities for the chat system
 */
export const useChatNavigation = () => {
  const [, setLocation] = useLocation();

  /**
   * Navigate to the messages page with optional session selection
   */
  const navigateToMessages = ({ sessionId, replace = false }: NavigateToMessagesOptions = {}) => {
    const baseUrl = '/messages';
    const url = sessionId ? `${baseUrl}?sessionId=${encodeURIComponent(sessionId)}` : baseUrl;

    if (replace) {
      // Replace current history entry
      window.history.replaceState(null, '', url);
      setLocation(url);
    } else {
      // Add new history entry
      setLocation(url);
    }
  };

  /**
   * Navigate to a specific chat session
   */
  const navigateToSession = (sessionId: string, replace = false) => {
    navigateToMessages({ sessionId, replace });
  };

  return {
    navigateToMessages,
    navigateToSession,
  };
};

/**
 * Parse query parameters from the current URL
 */
export const parseUrlQuery = (search: string): ParsedQuery => {
  const params = new URLSearchParams(search);
  const query: ParsedQuery = {};

  for (const [key, value] of params.entries()) {
    query[key] = value;
  }

  return query;
};

/**
 * Get the sessionId from URL query parameters
 */
export const getSessionIdFromUrl = (): string | null => {
  const search = window.location.search;
  const query = parseUrlQuery(search);
  return query.sessionId || null;
};

/**
 * Remove query parameters from the current URL
 */
export const clearUrlQuery = () => {
  const url = window.location.pathname;
  window.history.replaceState(null, '', url);
};

/**
 * Update URL with new query parameters without navigation
 */
export const updateUrlQuery = (params: Record<string, string | null>) => {
  const currentUrl = new URL(window.location.href);
  const searchParams = currentUrl.searchParams;

  // Update or remove parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value === null) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, value);
    }
  });

  // Update URL without triggering navigation
  const newUrl = `${currentUrl.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
  window.history.replaceState(null, '', newUrl);
};

/**
 * Create a deep link to a specific chat session
 */
export const createSessionDeepLink = (sessionId: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/messages?sessionId=${encodeURIComponent(sessionId)}`;
};

/**
 * Check if the current page is the messages page
 */
export const isMessagesPage = (): boolean => {
  return window.location.pathname === '/messages';
};

/**
 * Navigate back to messages page without session selection
 */
export const navigateToMessagesHome = () => {
  const [, setLocation] = useLocation();
  return () => setLocation('/messages');
};